import {useState} from 'react';
import {Box, HStack, IconButton, Input, Text} from '@chakra-ui/react';
import {FolderTree, Plus, Trash} from 'lucide-react';
import {
    addAlbumChild,
    AlbumTreeNode,
    createAlbumNode,
    removeAlbumNode,
    renameAlbumNode,
} from '../domain/albumTree';
import {slugify} from '../domain/naming';

interface AlbumTreeEditorProps {
    /** The root album's folder name, shown read-only as the tree's top row. */
    rootFolderName: string;
    rootTitle: string;
    /** The root album's sub-albums. */
    value: AlbumTreeNode[];
    onChange: (value: AlbumTreeNode[]) => void;
}

const INDENT = 1.5; // rems, for each level of depth in the tree

/** Edits the sub-album hierarchy of a new album: sub-albums can be added under any album and renamed or removed. */
export function AlbumTreeEditor({rootFolderName, rootTitle, value, onChange}: AlbumTreeEditorProps) {
    // The album just added gets the focus, so its name can be typed right away.
    const [addedId, setAddedId] = useState<string | null>(null);

    function add(parentId: string | null) {
        const node = createAlbumNode();
        setAddedId(node.id);
        onChange(addAlbumChild(value, parentId, node));
    }

    function renderNode(node: AlbumTreeNode, depth: number) {
        return <Box key={node.id}>
            <HStack gap={2} pl={`${depth * INDENT}rem`} py={1}>
                <Input
                    size="sm"
                    flex="1"
                    value={node.name}
                    onChange={(e) => onChange(renameAlbumNode(value, node.id, e.target.value))}
                    placeholder="Alalbum neve"
                    aria-label="Alalbum neve"
                    autoFocus={node.id === addedId}
                />
                <Text fontSize="xs" hideBelow="sm" color="gray.500" minW="24" truncate>
                    {node.name.trim() ? `${slugify(node.name)}/` : ''}
                </Text>
                <IconButton
                    aria-label="Alalbum hozzáadása ehhez"
                    title="Alalbum hozzáadása ehhez"
                    size="xs"
                    variant="ghost"
                    onClick={() => add(node.id)}
                >
                    <Plus size={16}/>
                </IconButton>
                <IconButton
                    aria-label="Alalbum törlése"
                    title="Alalbum törlése (az alalbumaival együtt)"
                    size="xs"
                    variant="ghost"
                    colorPalette="red"
                    onClick={() => onChange(removeAlbumNode(value, node.id))}
                >
                    <Trash size={16}/>
                </IconButton>
            </HStack>
            {node.children.map((child) => renderNode(child, depth + 1))}
        </Box>;
    }

    return <Box borderWidth="1px" borderRadius="md" p={2}>
        <HStack gap={2} py={1}>
            <FolderTree size={16}/>
            <Text flex="1" fontWeight="semibold" truncate>{rootTitle.trim() || 'Fő album'}</Text>
            <Text fontSize="xs" color="gray.500" truncate>{rootFolderName && `${rootFolderName}/`}</Text>
            <IconButton
                aria-label="Alalbum hozzáadása a fő albumhoz"
                title="Alalbum hozzáadása a fő albumhoz"
                size="sm"
                variant="solid"
                colorPalette="blue"
                onClick={() => add(null)}
            >
                <Plus size={18}/>
            </IconButton>
        </HStack>
        {value.map((node) => renderNode(node, 1))}
        <Text fontSize="xs" color="gray.500" mt={3}>
            A rendezvénycímkék csak a fő albumba, a fotósok listája és a web, archive, plazma mappák csak a legalsó szintű albumokba kerülnek.
        </Text>
    </Box>;
}
